import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close,
  Print,
  Article,
  Fullscreen,
  FullscreenExit,
  Refresh,
} from '@mui/icons-material';
import templateService, { Template } from '../../services/templateService';
import invoiceService from '../../services/invoiceService';
import { PdfExportButton } from '../contract/PdfExportButton';

interface ReciboModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number;
}

const ReciboModal: React.FC<ReciboModalProps> = ({ open, onClose, invoiceId }) => {
  const [availableTemplates, setAvailableTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('');
  const [renderedHtml, setRenderedHtml] = useState<string>('');
  const [invoiceInfo, setInvoiceInfo] = useState<{ id: number; clienteName: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const previewRef = useRef<HTMLDivElement>(null);

  // Verifica se é um recibo cupom (80mm)
  const isCupomReceipt = selectedTemplateName.includes('Recibo Cupom');

  useEffect(() => {
    if (open && invoiceId) {
      loadAvailableTemplates();
    }
  }, [open, invoiceId]);

  const loadAvailableTemplates = async () => {
    try {
      setTemplatesLoading(true);
      setError(null);

      const response = await templateService.getTemplates({
        type: 'recibo',
        isActive: true,
        limit: 50
      });

      setAvailableTemplates(response.templates || []);

    } catch (err: any) {
      setError('Erro ao carregar templates disponíveis: ' + (err.message || 'Erro desconhecido'));
      setAvailableTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleTemplateSelect = async (templateId: number) => {
    if (!templateId) return;

    try {
      setLoading(true);
      setError(null);

      // Chama o backend para renderizar o template com variáveis já substituídas
      const response = await invoiceService.renderReceiptTemplate(invoiceId, templateId);

      if (response.success && response.data) {
        setRenderedHtml(response.data.html);
        setSelectedTemplateName(response.data.templateTitle);
        setInvoiceInfo({
          id: response.data.invoiceId,
          clienteName: response.data.clienteName,
        });
      } else {
        throw new Error((response as any).message || 'Erro ao renderizar template');
      }

    } catch (err: any) {
      setError('Erro ao renderizar template: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!renderedHtml.trim()) {
      setError('Selecione um template para imprimir');
      return;
    }

    try {
      // Define configurações de impressão baseado no tipo de recibo
      const pageSize = isCupomReceipt ? '80mm auto' : 'A4';
      const pageMargin = isCupomReceipt ? '5mm' : '0.5in';
      const bodyPadding = isCupomReceipt ? '10px' : '20px';
      const fontSize = isCupomReceipt ? '11px' : '14px';

      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Recibo de Pagamento - Fatura #${invoiceInfo?.id || invoiceId}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                padding: ${bodyPadding}; 
                margin: 0;
                line-height: 1.6;
                color: #333;
                word-wrap: break-word;
                font-size: ${fontSize};
              }
              @media print { 
                body { margin: 0; padding: ${bodyPadding}; } 
                @page { 
                  size: ${pageSize}; 
                  margin: ${pageMargin}; 
                }
              }
              h1, h2, h3 { 
                color: #2c3e50; 
                margin-top: 0; 
                margin-bottom: 1rem;
                page-break-after: avoid;
                font-size: ${isCupomReceipt ? '14px' : '18px'};
              }
              p { 
                margin-bottom: 0.75rem; 
                text-align: ${isCupomReceipt ? 'left' : 'justify'};
                word-wrap: break-word;
                hyphens: auto;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 1rem 0;
                page-break-inside: avoid;
                font-size: ${isCupomReceipt ? '10px' : '13px'};
              }
              table th, table td {
                border: 1px solid #ddd;
                padding: ${isCupomReceipt ? '4px' : '8px'};
                text-align: left;
                word-wrap: break-word;
              }
              table th {
                background-color: #f8f9fa;
                font-weight: bold;
              }
              .no-break { page-break-inside: avoid; }
            </style>
          </head>
          <body>
            <div class="recibo-content">
              ${renderedHtml}
            </div>
            <script>
              window.onload = function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              };
            </script>
          </body>
          </html>
        `);
        printWindow.document.close();
      }

    } catch (err: any) {
      setError('Erro ao imprimir recibo: ' + (err.message || 'Erro desconhecido'));
    }
  };

  const renderPreviewContent = () => {
    if (!renderedHtml.trim()) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          color="text.secondary"
        >
          <Article sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" gutterBottom>
            Selecione um template
          </Typography>
          <Typography variant="body2">
            Escolha um template de recibo para visualizar
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          '& h1, & h2, & h3': {
            color: theme.palette.primary.main,
            marginTop: 0,
            marginBottom: theme.spacing(2)
          },
          '& p': {
            marginBottom: theme.spacing(1),
            lineHeight: 1.6
          },
          '& strong': {
            fontWeight: 600
          },
          '& ul, & ol': {
            paddingLeft: theme.spacing(3)
          },
          '& li': {
            marginBottom: theme.spacing(0.5)
          }
        }}
        dangerouslySetInnerHTML={{ __html: renderedHtml }}
      />
    );
  };

  const handleClose = () => {
    setSelectedTemplateId('');
    setSelectedTemplateName('');
    setRenderedHtml('');
    setInvoiceInfo(null);
    setError(null);
    setFullscreen(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={fullscreen ? false : "md"}
      fullWidth
      fullScreen={fullscreen || isMobile}
      PaperProps={{
        sx: {
          height: fullscreen || isMobile ? '100vh' : 'auto',
          maxHeight: fullscreen || isMobile ? 'none' : '90vh',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 1
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Article color="primary" />
          <Box>
            <Typography variant="h6" component="div">
              Recibo de Pagamento
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fatura #{invoiceInfo?.id || invoiceId} {invoiceInfo?.clienteName ? `- ${invoiceInfo.clienteName}` : ''}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title={fullscreen ? "Sair da tela cheia" : "Tela cheia"}>
            <IconButton onClick={() => setFullscreen(!fullscreen)}>
              {fullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Fechar">
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 2,
          overflow: 'auto',
          bgcolor: '#f5f5f5',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Seletor de Template */}
        <Box sx={{ mt: 2, mb: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Selecione um Template de Recibo</InputLabel>
            <Select
              value={selectedTemplateId}
              label="Selecione um Template de Recibo"
              onChange={(e) => {
                const templateId = e.target.value as number;
                setSelectedTemplateId(templateId);
                if (templateId) {
                  handleTemplateSelect(templateId);
                }
              }}
              disabled={templatesLoading || loading}
              startAdornment={
                templatesLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null
              }
            >
              <MenuItem value="">
                <em>Selecione um template</em>
              </MenuItem>
              {availableTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Article fontSize="small" />
                    <Box>
                      <Typography variant="body2">{template.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Atualizado em {new Date(template.updatedAt || '').toLocaleDateString('pt-BR')}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Preview do Recibo */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            minHeight: '100%',
          }}
        >
          <Paper
            ref={previewRef}
            elevation={3}
            sx={{
              width: '100%',
              maxWidth: isCupomReceipt ? '80mm' : '210mm',
              minHeight: fullscreen ? 'calc(100vh - 200px)' : 'auto',
              p: 2,
              bgcolor: 'white',
              color: 'black',
              fontSize: isCupomReceipt ? '11px' : '14px',
              lineHeight: 1.6,
              fontFamily: 'Arial, sans-serif',
              boxShadow: theme.shadows[8],
              '& *': {
                maxWidth: '100%',
                wordWrap: 'break-word',
              },
              '& h1, & h2, & h3': {
                fontSize: isCupomReceipt ? '14px' : '18px',
              },
              '& table': {
                fontSize: isCupomReceipt ? '10px' : '13px',
              },
              '& p': {
                textAlign: isCupomReceipt ? 'left' : 'justify',
              }
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
              </Box>
            ) : (
              renderPreviewContent()
            )}
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.5,
          gap: 1
        }}
      >
        <Button onClick={handleClose}>
          Fechar
        </Button>

        <PdfExportButton
          htmlContent={renderedHtml}
          filename={`recibo-fatura-${invoiceInfo?.id || invoiceId}.pdf`}
          disabled={!renderedHtml.trim()}
        />

        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={handlePrint}
          disabled={!renderedHtml.trim()}
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReciboModal;
